/* ==========================================================================
   MyPause — site fixes (JS)
   Version: 1.2.0 — 2026/09/21
   Hand-authored. Loaded site-wide (deferred) from `tool.head-tags.liquid`.

   WHY THIS FILE EXISTS
   `assets/layouts.theme.js` is compiled webpack output and its source repo
   is no longer available, so it cannot be regenerated. Fixes to theme JS
   behaviour therefore live here instead of being edited into the build
   artefact. (Same situation as `site-fixes.css` — see its header.)

   --------------------------------------------------------------------------
   1. Mega-menu overlay gets stuck open
   Reported 21/09/2026. Hover a top-level nav item ("Shop"/"Learn"), then
   move the cursor out of the page — e.g. up into the URL bar — and the dark
   page overlay stays forever. Page scroll is locked too.

   THE BUG
   The theme JS opens the menu on `mouseenter` of any nav link with children:
   it adds `is-menu-open` to <header class="c-header"> (which reveals the
   `.c-main-menu__background` overlay via CSS) and `is-scroll-disabled` to
   <body> (which locks scrolling). But the ONLY hover-based close is a
   `mouseleave` listener on the dropdown panel itself
   (`[data-submenu-container]`). If the cursor never enters the panel —
   straight up to the URL bar, or sideways to the cart icon — that listener
   never fires, `closeMenu` never runs, and the overlay stays. The overlay is
   `pointer-events: none`, so it cannot even be clicked away.

   THE FIX
   On desktop (the theme's 1100px breakpoint, matching `site-fixes.css`):
     a) `mouseleave` on each level-1 nav <li> that owns a dropdown panel —
        the panel is a DOM child of the <li>, so hovering into the panel
        keeps it open; leaving the item AND its panel closes it.
     b) `mouseleave` on <html> as a safety net for the cursor leaving the
        viewport entirely (fast moves can skip the <li>'s own event).
   Closing is done by dispatching a synthetic `mouseleave` on a
   `[data-submenu-container]`, which invokes the theme's own bound
   `closeMenu` and keeps its internal state (menuLevel) consistent. If the
   theme listener is somehow absent, a direct class-removal fallback runs.
   Both paths are guarded on the header actually having `is-menu-open`, so
   the mobile menu (different mechanism, below 1100px) is untouched.

   --------------------------------------------------------------------------
   2. Overlay disappears while a click-pinned menu stays open
   Reported 21/09/2026. Click "Shop" (the theme preventDefaults it, so the
   link keeps focus), then move the cursor away: the `:focus-within` rule in
   `site-fixes.css` holds the panel open, but the hover-out close from fix 1
   removed `is-menu-open`, hiding the overlay behind the open panel.

   THE FIX (behaviour chosen by the client 21/09/2026)
   A click PINS the menu open, overlay included:
     - The hover-out close (fix 1) is skipped while a level-1 item holds
       focus, so panel and overlay stay together.
     - The THEME's own `mouseleave` listener on `[data-submenu-container]`
       (see fix 1) would still close the menu when the cursor exits through
       the panel — the usual path for a real mouse. A capture-phase
       `mouseleave` listener on `document` stops propagation before the
       theme's target-phase listener runs, but ONLY while pinned, so the
       normal hover-out close is untouched.
     - Pressing Escape, pressing/clicking anywhere outside the header, or
       tabbing focus out of the menu force-closes: the focused link is
       blurred (releasing the CSS `:focus-within` hold) FIRST — which also
       lets the synthetic close event past the capture guard — and the menu
       is closed.
     - Hovering a DIFFERENT level-1 item blurs the pinned one, otherwise two
       panels would be open at once (the stacking bug documented in
       `site-fixes.css`).
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 1100px)');

  function init() {
    var header = document.querySelector('.c-header');
    var menu = header && header.querySelector('[data-main-menu]');
    if (!header || !menu) return;

    var panels = menu.querySelectorAll('[data-submenu-container]');
    if (!panels.length) return;

    /* The level-1 item pinned open by click/keyboard focus, if any. */
    function pinnedItem() {
      return menu.querySelector('.c-main-menu__item.is-level-1:focus-within');
    }

    /* Release the CSS `:focus-within` hold by blurring the focused link. */
    function unpin() {
      if (document.activeElement && menu.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }

    /* force=true also closes a click-pinned menu (blurring it first);
       force=false is the hover-out path, which respects the pin. */
    function closeMegaMenu(force) {
      if (!DESKTOP.matches) return;
      if (!header.classList.contains('is-menu-open')) return;
      if (!force && pinnedItem()) return;
      if (force) unpin();

      /* Preferred: trigger the theme's own closeMenu via its mouseleave
         listener on the panel, keeping its internal state in sync. */
      panels[0].dispatchEvent(new Event('mouseleave'));

      /* Fallback: if the theme listener didn't run (it only attaches when
         the page loads at desktop width), clear the classes directly. */
      if (header.classList.contains('is-menu-open')) {
        header.classList.remove('is-menu-open');
        menu.querySelectorAll('[data-menu-submenu]').forEach(function (el) {
          el.classList.remove('is-submenu-open');
        });
        document.body.classList.remove('is-scroll-disabled');
        document.documentElement.classList.remove('is-scroll-disabled');
      }
    }

    var closeOnLeave = function () { closeMegaMenu(false); };

    panels.forEach(function (panel) {
      var item = panel.closest('.c-main-menu__item.is-level-1');
      if (!item) return;

      /* (1a) Leaving a nav item (and its dropdown panel, a DOM child of the
         <li>) closes the menu — covers URL bar, cart icon, anywhere. */
      item.addEventListener('mouseleave', closeOnLeave);

      /* (2) Hovering a different item releases the pinned one so two
         panels are never open at once. */
      item.addEventListener('mouseenter', function () {
        var pinned = pinnedItem();
        if (pinned && pinned !== item) unpin();
      });
    });

    /* (1b) Safety net: cursor leaves the viewport entirely. */
    document.documentElement.addEventListener('mouseleave', closeOnLeave);

    /* (2) While pinned, block the THEME's own panel-mouseleave closeMenu.
       Capture phase runs on ancestors before the panel's own listeners,
       so stopPropagation here prevents the theme's close entirely. The
       forced-close path unpins (blurs) before dispatching its synthetic
       mouseleave, so it passes this guard. */
    document.addEventListener('mouseleave', function (e) {
      var pinned = DESKTOP.matches && pinnedItem();
      if (
        pinned &&
        e.target instanceof Element &&
        e.target.matches('[data-submenu-container]') &&
        pinned.contains(e.target)
      ) {
        e.stopPropagation();
      }
    }, true);

    /* (2) Force-close triggers for a click-pinned menu. */
    document.addEventListener('pointerdown', function (e) {
      if (!header.classList.contains('is-menu-open')) return;
      if (!header.contains(e.target)) closeMegaMenu(true);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMegaMenu(true);
    });

    menu.addEventListener('focusout', function (e) {
      if (e.relatedTarget && !menu.contains(e.relatedTarget)) {
        closeMegaMenu(true);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
